import * as THREE from 'three/webgpu';
import {
    type FeatureLine,
    FeatureResultMesh,
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
    isFeatureSurface,
} from '../../mesh/feature';
import { isDatumPoint } from '../../reference/DatumPoint';
import { buildCircleFromThreePoints2D } from '../../tool/buildCircleFromThreePoints2D';
import { resolveOwningFeatureResultMesh } from '../../utils/feature/resolveFeatureSceneMesh';
import { MeasureGeometryKind } from '../types/measure-property-types';
import type {
    MeasureArcEdgeMetrics,
    MeasureBoundedFaceMetrics,
    MeasureCircularSurfaceFaceMetrics,
    MeasureFullCircleEdgeMetrics,
    MeasureMetricQueryContext,
    MeasurePlaneFaceMetrics,
    MeasureStraightEdgeMetrics,
    MeasureVertexMetrics,
} from '../types/measure-property-types';
import type { MeasureCircleDescriptor } from '../utils/measureArcCircleReferencePoints';
import {
    averageMeterScalars,
    computeDistance3DMeters,
    computeDistance3DMillimeter,
    computeTriangleAreaMeters,
    fitCircleFromPlane2DPointsMeters,
    formatMeasureNumber,
    formatMeasurePointTuple,
    meterAreaToSquareMillimeter,
    meterCoordinateTupleToMillimeterTuple,
    meterLengthToMillimeter,
    meterRadiusToDiameterMillimeter,
    meterRadiusToMillimeter,
    sumMeterLengths,
} from '../math/MeasureNumericKernel';

export { formatMeasureNumber, formatMeasurePointTuple };

/** 判断闭合圆边的参数跨度容差（相对 2π）。 */
const FULL_CIRCLE_PARAMETER_SPAN_TOLERANCE = 0.08;

/** 判断首尾重合的相对半径容差。 */
const FULL_CIRCLE_ENDPOINT_TOLERANCE_RATIO = 0.05;

/** 解析圆曲面中轴线的几何容差。 */
const CIRCULAR_SURFACE_AXIS_EPSILON = 1e-9;

/**
 * 测量度量计算器。
 *
 * 职责：在主线程基于拓扑快照、BRep 描述符与结果体网格计算测量数值。
 * 说明：精确 Puppy CAD 内核 度量应在 Worker 预计算；当前实现优先消费已有快照与网格，保证测量浮窗同步可用。
 */
export class MeasureMetricCalculator {
    /**
     * 计算顶点三维坐标。
     *
     * @param context 度量上下文。
     * @returns 顶点度量；无法计算时返回 null。
     */
    public computeVertexMetrics(context: MeasureMetricQueryContext): MeasureVertexMetrics | null {
        const { sceneObject, brepDescriptor } = context;

        if (isFeaturePoint(sceneObject)) {
            const worldPoint = sceneObject.getWorldPoint();
            return { worldPoint: toPointTuple(worldPoint) };
        }

        // 修正：isDatumPoint通过三维点坐标获取方法应为getWorldPoint，无getWorldPosition方法
        if (isDatumPoint(sceneObject)) {
            const worldPoint = sceneObject.getWorldPoint();
            return { worldPoint: toPointTuple(worldPoint) };
        }


        if (brepDescriptor?.entityKind === 'vertex') {
            return { worldPoint: meterCoordinateTupleToMillimeterTuple(brepDescriptor.point) };
        }

        return null;
    }

    /**
     * 计算直线边长度。
     *
     * @param context 度量上下文。
     * @returns 直线度量；无法计算时返回 null。
     */
    public computeStraightEdgeMetrics(context: MeasureMetricQueryContext): MeasureStraightEdgeMetrics | null {
        const length = resolveEdgeLength(context);
        if (length === null || !Number.isFinite(length)) {
            return null;
        }
        return { length: meterLengthToMillimeter(length) };
    }

    /**
     * 计算圆弧边半径与圆心。
     *
     * @param context 度量上下文。
     * @returns 圆弧度量；无法计算时返回 null。
     */
    public computeArcEdgeMetrics(context: MeasureMetricQueryContext): MeasureArcEdgeMetrics | null {
        const circle = resolveEdgeCircleFit(context);
        if (!circle) {
            return null;
        }
        const radiusMillimeter = meterRadiusToMillimeter(circle.radius);
        if (radiusMillimeter === null) {
            return null;
        }
        return {
            radius: radiusMillimeter,
            center: toPointTuple(circle.center),
        };
    }

    /**
     * 计算整圆边直径与圆心。
     *
     * @param context 度量上下文。
     * @returns 整圆边度量；无法计算时返回 null。
     */
    public computeFullCircleEdgeMetrics(context: MeasureMetricQueryContext): MeasureFullCircleEdgeMetrics | null {
        const circle = resolveEdgeCircleFit(context);
        if (!circle) {
            return null;
        }
        const diameterMillimeter = meterRadiusToDiameterMillimeter(circle.radius);
        if (diameterMillimeter === null) {
            return null;
        }
        return {
            diameter: diameterMillimeter,
            center: toPointTuple(circle.center),
        };
    }

    /**
     * 计算平面面积与周长。
     *
     * @param context 度量上下文。
     * @returns 平面度量；无法计算时返回 null。
     */
    public computePlaneFaceMetrics(context: MeasureMetricQueryContext): MeasurePlaneFaceMetrics | null {
        return this.computeBoundedSurfaceFaceMetrics(context);
    }

    /**
     * 计算有边界面域面积与周长（平面/圆锥/圆环/B样条等）。
     *
     * 设计说明：面积与周长均基于三角网格与拓扑邻接边，不依赖解析曲面参数。
     *
     * @param context 度量上下文。
     * @returns 面域度量；无法计算时返回 null。
     */
    public computeBoundedSurfaceFaceMetrics(
        context: MeasureMetricQueryContext,
    ): MeasureBoundedFaceMetrics | null {
        const area = resolveFaceMeshArea(context);
        const perimeter = resolveFaceBoundaryPerimeter(context);
        if (area === null || perimeter === null) {
            return null;
        }
        return {
            area: meterAreaToSquareMillimeter(area),
            perimeter: meterLengthToMillimeter(perimeter),
        };
    }

    /**
     * 计算圆曲面（圆柱/球面）半径、面积与周长。
     *
     * @param context 度量上下文。
     * @returns 圆曲面度量；无法计算时返回 null。
     */
    public computeCircularSurfaceFaceMetrics(
        context: MeasureMetricQueryContext,
    ): MeasureCircularSurfaceFaceMetrics | null {
        const area = resolveFaceMeshArea(context);
        const perimeter = resolveFaceBoundaryPerimeter(context);
        const radius = resolveCircularSurfaceRadius(context);
        const radiusMillimeter = radius === null ? null : meterRadiusToMillimeter(radius);
        if (area === null || perimeter === null || radiusMillimeter === null) {
            return null;
        }
        return {
            area: meterAreaToSquareMillimeter(area),
            perimeter: meterLengthToMillimeter(perimeter),
            radius: radiusMillimeter,
        };
    }

    /**
     * 解析边在世界坐标下的折线采样点列。
     *
     * 设计说明：供测量最近点求解复用，与边长度/圆弧拟合同源。
     *
     * @param context 度量上下文。
     * @returns 世界坐标点列。
     */
    public resolveEdgeWorldPolylinePoints(context: MeasureMetricQueryContext): THREE.Vector3[] {
        return resolveEdgeSampledWorldPoints(context);
    }
}


/**
 * 从度量上下文读取权威拓扑快照。
 *
 * @param context 度量上下文。
 * @param resultMesh 所属结果体。
 * @returns 拓扑快照；不存在时返回 null。
 */
function resolveMetricTopologySnapshot(
    context: MeasureMetricQueryContext,
    resultMesh: FeatureResultMesh | null,
): TopologySnapshot | null {
    if (context.topologySnapshot) {
        return context.topologySnapshot;
    }
    return resultMesh?.getTopologySnapshot() ?? null;
}

/**
 * 根据权威拓扑顶点索引读取快照顶点。
 *
 * 设计说明：
 * `vertexIndex` 是 `TopologySnapshotVertex.index`，不是 mesh geometry position 下标。
 *
 * @param snapshot 拓扑快照。
 * @param vertexIndex 权威拓扑顶点索引。
 * @returns 快照顶点；未命中时返回 null。
 */
function resolveTopologySnapshotVertex(
    snapshot: TopologySnapshot,
    vertexIndex: number,
): TopologySnapshotVertex | null {
    return snapshot.vertices.find((vertex) => vertex.index === vertexIndex) ?? null;
}

/**
 * 将快照中的局部坐标点变换到世界坐标。
 *
 * @param resultMesh 结果体。
 * @param localPoint 局部坐标点。
 * @returns 世界坐标点。
 */
function topologyLocalPointToWorld(
    resultMesh: FeatureResultMesh,
    localPoint: TopologyPoint3D,
): THREE.Vector3 {
    resultMesh.updateWorldMatrix(true, false);
    return new THREE.Vector3(localPoint[0], localPoint[1], localPoint[2])
        .applyMatrix4(resultMesh.matrixWorld);
}

/**
 * 从边快照的 vertexAIndex / vertexBIndex 解析世界坐标端点。
 *
 * @param resultMesh 结果体。
 * @param snapshot 拓扑快照。
 * @param edgeSnapshot 边拓扑快照。
 * @returns 世界坐标端点；无法解析时返回空数组。
 */
function resolveEdgeTopologyEndpointWorldPoints(
    resultMesh: FeatureResultMesh,
    snapshot: TopologySnapshot,
    edgeSnapshot: TopologySnapshotEdge,
): THREE.Vector3[] {
    const vertexA = resolveTopologySnapshotVertex(snapshot, edgeSnapshot.vertexAIndex);
    const vertexB = resolveTopologySnapshotVertex(snapshot, edgeSnapshot.vertexBIndex);
    if (!vertexA || !vertexB) {
        return [];
    }
    return [
        topologyLocalPointToWorld(resultMesh, vertexA.position),
        topologyLocalPointToWorld(resultMesh, vertexB.position),
    ];
}

/**
 * 读取 FeatureLine 代理几何上的世界坐标折线点。
 *
 * @param line 边代理。
 * @param resultMesh 结果体。
 * @returns 世界坐标点列。
 */
function resolveFeatureLineGeometryWorldPoints(
    line: FeatureLine,
    resultMesh: FeatureResultMesh | null,
): THREE.Vector3[] {
    const positionAttribute = line.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count < 2) {
        return [];
    }

    const worldMatrix = resolveLineWorldMatrix(line, resultMesh);
    const points: THREE.Vector3[] = [];
    const point = new THREE.Vector3();
    for (let index = 0; index < positionAttribute.count; index += 1) {
        point.set(
            positionAttribute.getX(index),
            positionAttribute.getY(index),
            positionAttribute.getZ(index),
        ).applyMatrix4(worldMatrix);
        points.push(point.clone());
    }
    return points;
}

/**
 * 将快照采样点变换为世界坐标折线点。
 *
 * @param resultMesh 结果体。
 * @param sampledPoints 边采样点。
 * @returns 世界坐标点列。
 */
function resolveSampledTopologyPointsToWorld(
    resultMesh: FeatureResultMesh,
    sampledPoints: TopologyPoint3D[],
): THREE.Vector3[] {
    resultMesh.updateWorldMatrix(true, false);
    const matrixWorld = resultMesh.matrixWorld;
    return sampledPoints.map((tuple) => (
        new THREE.Vector3(tuple[0], tuple[1], tuple[2]).applyMatrix4(matrixWorld)
    ));
}

/**
 * 解析边度量用的完整世界坐标采样点。
 *
 * 设计说明：
 * 1. 圆弧/整圆/样条边的真实长度必须沿 Puppy CAD 内核 全量采样折线累加，不能只用首尾弦长；
 * 2. 直线边没有 sampledPoints 时，通过 vertexAIndex / vertexBIndex 回到 TopologySnapshot.vertices；
 * 3. vertexAIndex / vertexBIndex 不是 resultMesh.geometry position 下标，禁止混用；
 * 4. 该函数供边长、圆拟合与整圆判定共用，避免“角点”与“弧长”语义混用。
 *
 * @param context 度量上下文。
 * @returns 世界坐标点列；无法解析时返回空数组。
 */
function resolveEdgeSampledWorldPoints(context: MeasureMetricQueryContext): THREE.Vector3[] {
    const { sceneObject, edgeSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    const snapshot = resolveMetricTopologySnapshot(context, resultMesh);

    if (resultMesh && edgeSnapshot?.sampledPoints && edgeSnapshot.sampledPoints.length > 0) {
        return resolveSampledTopologyPointsToWorld(resultMesh, edgeSnapshot.sampledPoints);
    }

    if (isFeatureLine(sceneObject)) {
        const proxyPoints = resolveFeatureLineGeometryWorldPoints(sceneObject, resultMesh);
        if (proxyPoints.length >= 2) {
            return proxyPoints;
        }
    }

    if (resultMesh && snapshot && edgeSnapshot) {
        const endpointPoints = resolveEdgeTopologyEndpointWorldPoints(resultMesh, snapshot, edgeSnapshot);
        if (endpointPoints.length >= 2) {
            return endpointPoints;
        }
    }

    return [];
}

/**
 * 解析边世界坐标首尾角点。
 *
 * 设计说明：
 * 仅用于需要拓扑端点、而非曲线弧长的场景；边长/周长应优先使用 {@link resolveEdgeSampledWorldPoints}。
 *
 * @param context 度量上下文。
 * @returns 世界坐标点列（通常含首尾点）。若无法解析则返回空数组。
 */
function resolveEdgeEndpointWorldPoints(context: MeasureMetricQueryContext): THREE.Vector3[] {
    const sampledPoints = resolveEdgeSampledWorldPoints(context);
    if (sampledPoints.length >= 2) {
        return [
            sampledPoints[0]!.clone(),
            sampledPoints[sampledPoints.length - 1]!.clone(),
        ];
    }
    if (sampledPoints.length === 1) {
        return [sampledPoints[0]!.clone()];
    }
    return [];
}

/**
 * 累加折线世界坐标长度。
 *
 * @param points 世界坐标点列。
 * @returns 折线总长度。
 */
function sumPolylineLength(points: THREE.Vector3[]): number {
    const segmentLengthsMeters: number[] = [];
    for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1]!;
        const current = points[index]!;
        segmentLengthsMeters.push(computeDistance3DMeters(
            previous.x,
            previous.y,
            previous.z,
            current.x,
            current.y,
            current.z,
        ));
    }
    return sumMeterLengths(segmentLengthsMeters);
}

/**
 * 解析边世界矩阵。
 *
 * @param line 边代理。
 * @param resultMesh 结果体。
 * @returns 世界矩阵。
 */
function resolveLineWorldMatrix(line: THREE.Object3D, resultMesh: FeatureResultMesh | null): THREE.Matrix4 {
    if (line.parent) {
        line.updateWorldMatrix(true, false);
        return line.matrixWorld;
    }
    if (resultMesh) {
        resultMesh.updateWorldMatrix(true, false);
        return resultMesh.matrixWorld;
    }
    line.updateWorldMatrix(true, false);
    return line.matrixWorld;
}

/**
 * 解析边长度。
 *
 * @param context 度量上下文。
 * @returns 边长度；无法计算时返回 null。
 */
function resolveEdgeLength(context: MeasureMetricQueryContext): number | null {
    const { brepDescriptor } = context;
    if (brepDescriptor?.entityKind === 'edge' && brepDescriptor.curveKind === 'line') {
        return distanceBetween(
            brepDescriptor.start,
            brepDescriptor.end,
        );
    }

    const worldPoints = resolveEdgeSampledWorldPoints(context);
    if (worldPoints.length < 2) {
        return null;
    }

    const totalLengthMeters = sumPolylineLength(worldPoints);
    return Number.isFinite(totalLengthMeters) ? totalLengthMeters : null;
}

/**
 * 对边采样点做圆拟合。
 *
 * @param context 度量上下文。
 * @returns 圆心与半径；无法拟合时返回 null。
 */
function resolveEdgeCircleFit(
    context: MeasureMetricQueryContext,
): { center: THREE.Vector3; radius: number } | null {
    const worldPoints = resolveEdgeSampledWorldPoints(context);
    return fitCircleFromWorldPoints(worldPoints);
}

/**
 * 判断边是否为整圆闭合边。
 *
 * @param context 度量上下文。
 * @param circle 圆拟合结果。
 * @returns 是否为整圆边。
 */
export function isFullCircleEdge(
    context: MeasureMetricQueryContext,
    circle: { center: THREE.Vector3; radius: number } | null,
): boolean {
    const { edgeSnapshot } = context;
    if (!edgeSnapshot || !circle || circle.radius <= Number.EPSILON) {
        return false;
    }

    if (edgeSnapshot.vertexAIndex === edgeSnapshot.vertexBIndex) {
        return true;
    }

    const worldPoints = resolveEdgeSampledWorldPoints(context);
    if (worldPoints.length < 3) {
        return false;
    }

    const first = worldPoints[0]!;
    const last = worldPoints[worldPoints.length - 1]!;
    if (first.distanceTo(last) > circle.radius * FULL_CIRCLE_ENDPOINT_TOLERANCE_RATIO) {
        return false;
    }

    const polylineLength = sumPolylineLength(worldPoints);
    const fullCircumference = Math.PI * 2 * circle.radius;
    return Math.abs(polylineLength - fullCircumference) <= fullCircumference * FULL_CIRCLE_PARAMETER_SPAN_TOLERANCE;
}

/**
 * 平面拟合基向量。
 */
interface PlaneFitBasis {
    /** 平面原点（用于投影）。 */
    origin: THREE.Vector3;
    /** 平面 U 轴。 */
    axisU: THREE.Vector3;
    /** 平面 V 轴。 */
    axisV: THREE.Vector3;
}

/**
 * 二维圆拟合结果。
 */
interface Plane2DCircleFit {
    /** 圆心 U 坐标。 */
    centerU: number;
    /** 圆心 V 坐标。 */
    centerV: number;
    /** 半径。 */
    radius: number;
}

/**
 * 圆曲面轴线参考解析输入。
 */
interface CircularSurfaceAxisReferenceInput {
    /** 当前测量上下文。 */
    context: MeasureMetricQueryContext;
    /** 所属结果体。 */
    resultMesh: FeatureResultMesh;
    /** 相邻圆形边界的拟合圆描述符。 */
    boundaryDescriptors: MeasureCircleDescriptor[];
}

/**
 * 基于世界坐标点列拟合圆（支持圆弧）。
 *
 * 设计说明：
 * 1. 点集可能是圆弧而非整圆，不能对三维点直接取均值当圆心；
 * 2. 先用 Newell 法估计拟合平面，再将点投影到平面二维坐标；
 * 3. 在二维上用最小二乘代数圆拟合（Kasa）求圆心与半径；
 * 4. 三点退化时使用 {@link buildCircleFromThreePoints2D} 精确外接圆。
 *
 * @param points 世界坐标点列。
 * @returns 圆心与半径；无法拟合时返回 null。
 */
function fitCircleFromWorldPoints(
    points: THREE.Vector3[],
): { center: THREE.Vector3; radius: number } | null {
    if (points.length < 3) {
        return null;
    }

    const planeBasis = estimatePlaneBasisFromWorldPoints(points);
    if (!planeBasis) {
        return null;
    }

    const planePoints = projectWorldPointsToPlane2D(points, planeBasis);
    const circle2d = fitCircleFromPlane2DPoints(planePoints);
    if (!circle2d) {
        return null;
    }

    const center = planeBasis.origin.clone()
        .addScaledVector(planeBasis.axisU, circle2d.centerU)
        .addScaledVector(planeBasis.axisV, circle2d.centerV);

    if (!Number.isFinite(circle2d.radius) || circle2d.radius <= Number.EPSILON) {
        return null;
    }

    return { center, radius: circle2d.radius };
}

/**
 * 用 Newell 法估计点集所在平面的正交基。
 *
 * @param points 世界坐标点列。
 * @returns 平面基；退化时返回 null。
 */
function estimatePlaneBasisFromWorldPoints(points: THREE.Vector3[]): PlaneFitBasis | null {
    const normal = new THREE.Vector3();
    for (let index = 0; index < points.length; index += 1) {
        const current = points[index]!;
        const next = points[(index + 1) % points.length]!;
        normal.x += (current.y - next.y) * (current.z + next.z);
        normal.y += (current.z - next.z) * (current.x + next.x);
        normal.z += (current.x - next.x) * (current.y + next.y);
    }

    if (normal.lengthSq() <= Number.EPSILON) {
        return null;
    }
    normal.normalize();

    const origin = new THREE.Vector3();
    points.forEach((point) => origin.add(point));
    origin.multiplyScalar(1 / points.length);

    const axisReference = Math.abs(normal.x) < 0.9
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 1, 0);
    const axisU = new THREE.Vector3().crossVectors(normal, axisReference).normalize();
    const axisV = new THREE.Vector3().crossVectors(normal, axisU);

    return { origin, axisU, axisV };
}

/**
 * 将世界坐标点投影到平面二维坐标。
 *
 * @param points 世界坐标点列。
 * @param basis 平面基。
 * @returns 二维坐标点列。
 */
function projectWorldPointsToPlane2D(
    points: THREE.Vector3[],
    basis: PlaneFitBasis,
): Array<{ x: number; y: number }> {
    const offset = new THREE.Vector3();
    return points.map((point) => {
        offset.copy(point).sub(basis.origin);
        return {
            x: offset.dot(basis.axisU),
            y: offset.dot(basis.axisV),
        };
    });
}

/**
 * 在二维平面点列上拟合圆。
 *
 * @param planePoints 二维坐标点列。
 * @returns 圆心与半径；无法拟合时返回 null。
 */
function fitCircleFromPlane2DPoints(
    planePoints: Array<{ x: number; y: number }>,
): Plane2DCircleFit | null {
    if (planePoints.length < 3) {
        return null;
    }

    if (planePoints.length === 3) {
        const p1 = planePoints[0]!;
        const p2 = planePoints[1]!;
        const p3 = planePoints[2]!;
        const circle = buildCircleFromThreePoints2D(
            new THREE.Vector2(p1.x, p1.y),
            new THREE.Vector2(p2.x, p2.y),
            new THREE.Vector2(p3.x, p3.y),
        );
        if (!circle) {
            return null;
        }
        return {
            centerU: circle.center.x,
            centerV: circle.center.y,
            radius: circle.radius,
        };
    }

    return fitCircleFromPlane2DPointsMeters(planePoints);
}

/**
 * 计算面的网格面积。
 *
 * @param context 度量上下文。
 * @returns 面积；无法计算时返回 null。
 */
function resolveFaceMeshArea(context: MeasureMetricQueryContext): number | null {
    const { sceneObject, faceSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    if (!resultMesh || !faceSnapshot) {
        return null;
    }

    const triangleRange = isFeatureFace(sceneObject)
        ? sceneObject.triangleRange
        : faceSnapshot.triangleRange;

    return computeTriangleRangeArea(resultMesh, triangleRange);
}

/**
 * 计算面边界总长度。
 *
 * @param context 度量上下文。
 * @returns 周长；无法计算时返回 null。
 */
function resolveFaceBoundaryPerimeter(context: MeasureMetricQueryContext): number | null {
    const { sceneObject, faceSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    if (!resultMesh || !faceSnapshot) {
        return null;
    }

    const topologySnapshot = resolveMetricTopologySnapshot(context, resultMesh);
    let totalPerimeter = 0;
    let measuredEdgeCount = 0;
    faceSnapshot.adjacentEdgeIndices.forEach((edgeIndex) => {
        /**
         * 此处循环获得的所有边的长度和，理论上是当前面的外边界和内环边界的总周长。
         * 注意：此为拓扑结构中“邻接边”集合，包含了所有属于该面的轮廓边（包括外圈和内圈洞）。
         * 若仅所有相邻边都连续组成一个闭环，则它们长度和即为面的真实周长（SolidWorks 中的 LOOP）。
         * 若包含未封闭情况（如模型破损或多余边），则实际周长可能大于真实物理边界。
         * 实际使用时已排除了 feature 识别失败场景，因此此和即为该面包括环在内的总周长，与 SOLIDWORKS 面属性一致。
         */
        const edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(edgeIndex);
  
        if (!edgeSnapshot) {
            return;
        }
        const edgeContext: MeasureMetricQueryContext = {
            sceneObject,
            topologyPick: null,
            brepDescriptor: null,
            edgeSnapshot,
            faceSnapshot: null,
            topologySnapshot,
        };
        const edgeLength = resolveEdgeLength(edgeContext);
        if (edgeLength === null || !Number.isFinite(edgeLength)) {
            return;
        }
        totalPerimeter += edgeLength;
        measuredEdgeCount += 1;
    });

    if (measuredEdgeCount <= 0) {
        return null;
    }
    return totalPerimeter;
}

/**
 * 解析圆曲面半径。
 *
 * 设计说明：
 * 1. 优先取边界圆边的拟合半径；
 * 2. 若无圆边，则退化为面网格顶点到质心平均距离。
 *
 * @param context 度量上下文。
 * @returns 半径；无法计算时返回 null。
 */
function resolveCircularSurfaceRadius(context: MeasureMetricQueryContext): number | null {
    const { sceneObject, faceSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    if (!resultMesh || !faceSnapshot) {
        return null;
    }

    for (const edgeIndex of faceSnapshot.adjacentEdgeIndices) {
        const edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(edgeIndex);
        if (!edgeSnapshot || edgeSnapshot.curveKind !== 'circle') {
            continue;
        }
        const edgeContext: MeasureMetricQueryContext = {
            sceneObject,
            topologyPick: null,
            brepDescriptor: null,
            edgeSnapshot,
            faceSnapshot: null,
        };
        const circle = resolveEdgeCircleFit(edgeContext);

        // let debug = 0;
        if (circle) {
            return circle.radius;
        }
    }

    const meshPoints = collectFaceMeshWorldPoints(resultMesh, faceSnapshot.triangleRange);
    const circle = fitCircleFromWorldPoints(meshPoints);
    return circle?.radius ?? null;
}

/**
 * 收集面三角范围内的世界坐标顶点。
 *
 * @param resultMesh 结果体。
 * @param triangleRange 三角形范围。
 * @returns 世界坐标顶点列表。
 */
function collectFaceMeshWorldPoints(
    resultMesh: FeatureResultMesh,
    triangleRange: [number, number],
): THREE.Vector3[] {
    const geometry = resultMesh.geometry;
    const positionAttribute = geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return [];
    }

    resultMesh.updateWorldMatrix(true, false);
    const matrixWorld = resultMesh.matrixWorld;
    const indexAttribute = geometry.getIndex();
    const point = new THREE.Vector3();
    const uniqueKeys = new Set<string>();
    const points: THREE.Vector3[] = [];

    for (let triangleIndex = triangleRange[0]; triangleIndex < triangleRange[1]; triangleIndex += 1) {
        for (let vertexOffset = 0; vertexOffset < 3; vertexOffset += 1) {
            const rawIndex = readTriangleVertexIndex(triangleIndex, vertexOffset, indexAttribute);
            const key = String(rawIndex);
            if (uniqueKeys.has(key)) {
                continue;
            }
            uniqueKeys.add(key);
            point.set(
                positionAttribute.getX(rawIndex),
                positionAttribute.getY(rawIndex),
                positionAttribute.getZ(rawIndex),
            ).applyMatrix4(matrixWorld);
            points.push(point.clone());
        }
    }
    return points;
}

/**
 * 计算三角形范围在世界空间中的总面积。
 *
 * @param mesh 结果体网格。
 * @param triangleRange 三角形范围。
 * @returns 面积。
 */
function computeTriangleRangeArea(
    mesh: FeatureResultMesh,
    triangleRange: [number, number],
): number | null {
    const geometry = mesh.geometry;
    const positionAttribute = geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return null;
    }

    mesh.updateWorldMatrix(true, false);
    const matrixWorld = mesh.matrixWorld;
    const indexAttribute = geometry.getIndex();
    const pointA = new THREE.Vector3();
    const pointB = new THREE.Vector3();
    const pointC = new THREE.Vector3();
    let totalAreaMeters = 0;

    for (let triangleIndex = triangleRange[0]; triangleIndex < triangleRange[1]; triangleIndex += 1) {
        readTriangleVertex(triangleIndex, 0, positionAttribute, indexAttribute, pointA).applyMatrix4(matrixWorld);
        readTriangleVertex(triangleIndex, 1, positionAttribute, indexAttribute, pointB).applyMatrix4(matrixWorld);
        readTriangleVertex(triangleIndex, 2, positionAttribute, indexAttribute, pointC).applyMatrix4(matrixWorld);
        totalAreaMeters += computeTriangleAreaMeters(
            pointB.x - pointA.x,
            pointB.y - pointA.y,
            pointB.z - pointA.z,
            pointC.x - pointA.x,
            pointC.y - pointA.y,
            pointC.z - pointA.z,
        );
    }

    return Number.isFinite(totalAreaMeters) ? totalAreaMeters : null;
}

/**
 * 读取三角形顶点索引。
 *
 * @param triangleIndex 三角形索引。
 * @param vertexOffset 顶点偏移。
 * @param indexAttribute 索引属性。
 * @returns 顶点索引。
 */
function readTriangleVertexIndex(
    triangleIndex: number,
    vertexOffset: number,
    indexAttribute: THREE.BufferAttribute | null,
): number {
    if (indexAttribute instanceof THREE.BufferAttribute) {
        return Number(indexAttribute.getX(triangleIndex * 3 + vertexOffset));
    }
    return triangleIndex * 3 + vertexOffset;
}

/**
 * 读取三角形顶点坐标到目标向量。
 *
 * @param triangleIndex 三角形索引。
 * @param vertexOffset 顶点偏移。
 * @param positionAttribute 位置属性。
 * @param indexAttribute 索引属性。
 * @param target 目标向量。
 * @returns 目标向量。
 */
function readTriangleVertex(
    triangleIndex: number,
    vertexOffset: number,
    positionAttribute: THREE.BufferAttribute,
    indexAttribute: THREE.BufferAttribute | null,
    target: THREE.Vector3,
): THREE.Vector3 {
    const vertexIndex = readTriangleVertexIndex(triangleIndex, vertexOffset, indexAttribute);
    return target.set(
        positionAttribute.getX(vertexIndex),
        positionAttribute.getY(vertexIndex),
        positionAttribute.getZ(vertexIndex),
    );
}

/**
 * 计算两点距离。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 距离。
 */
function distanceBetween(left: TopologyPoint3D, right: TopologyPoint3D): number {
    return computeDistance3DMeters(left[0], left[1], left[2], right[0], right[1], right[2]);
}

/**
 * 将 Vector3 转为坐标元组。
 *
 * @param vector 向量。
 * @returns 坐标元组。
 */
function toPointTuple(vector: THREE.Vector3): TopologyPoint3D {
    return meterCoordinateTupleToMillimeterTuple([vector.x, vector.y, vector.z]);
}

/**
 * 从度量上下文解析圆几何描述符（圆心、半径、平面法向）。
 *
 * 设计说明：供圆弧/圆测量策略复用边采样与平面拟合，避免在策略层重复拟合逻辑。
 *
 * @param context 度量上下文。
 * @param calculator 度量计算器。
 * @returns 圆描述符；无法解析时返回 null。
 */
export function resolveMeasureCircleDescriptorFromContext(
    context: MeasureMetricQueryContext,
    calculator: MeasureMetricCalculator,
): MeasureCircleDescriptor | null {
    const edgePoints = calculator.resolveEdgeWorldPolylinePoints(context);
    const edgeCircle = fitCircleFromWorldPoints(edgePoints);
    if (edgeCircle) {
        const basis = estimatePlaneBasisFromWorldPoints(edgePoints);
        if (!basis) {
            return null;
        }
        const normal = new THREE.Vector3().crossVectors(basis.axisU, basis.axisV).normalize();
        return {
            center: edgeCircle.center,
            radius: edgeCircle.radius,
            normal,
        };
    }

    const { sceneObject, faceSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    if (!resultMesh || !faceSnapshot) {
        return null;
    }

    const boundaryDescriptors = resolveCircularBoundaryDescriptors(context, resultMesh);
    const axisDescriptor = resolveCircularSurfaceAxisMidpointDescriptor({
        context,
        resultMesh,
        boundaryDescriptors,
    });
    if (axisDescriptor) {
        return axisDescriptor;
    }

    const firstBoundaryDescriptor = boundaryDescriptors[0];
    if (firstBoundaryDescriptor) {
        return firstBoundaryDescriptor;
    }

    const meshPoints = collectFaceMeshWorldPoints(resultMesh, faceSnapshot.triangleRange);
    const meshCircle = fitCircleFromWorldPoints(meshPoints);
    if (!meshCircle) {
        return null;
    }
    const meshBasis = estimatePlaneBasisFromWorldPoints(meshPoints);
    if (!meshBasis) {
        return null;
    }
    const meshNormal = new THREE.Vector3().crossVectors(meshBasis.axisU, meshBasis.axisV).normalize();
    return {
        center: meshCircle.center,
        radius: meshCircle.radius,
        normal: meshNormal,
    };
}

/**
 * 解析圆曲面相邻的圆形边界描述符。
 *
 * @param context 度量上下文。
 * @param resultMesh 所属结果体。
 * @returns 圆形边界描述符列表。
 */
function resolveCircularBoundaryDescriptors(
    context: MeasureMetricQueryContext,
    resultMesh: FeatureResultMesh,
): MeasureCircleDescriptor[] {
    const { sceneObject, faceSnapshot, topologySnapshot } = context;
    if (!faceSnapshot) {
        return [];
    }

    const descriptors: MeasureCircleDescriptor[] = [];
    for (const edgeIndex of faceSnapshot.adjacentEdgeIndices) {
        const edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(edgeIndex);
        if (!edgeSnapshot || edgeSnapshot.curveKind !== 'circle') {
            continue;
        }

        const edgeContext: MeasureMetricQueryContext = {
            sceneObject,
            topologyPick: null,
            brepDescriptor: null,
            edgeSnapshot,
            faceSnapshot: null,
            topologySnapshot,
        };
        const descriptor = buildMeasureCircleDescriptorFromEdgeContext(edgeContext);
        if (descriptor) {
            descriptors.push(descriptor);
        }
    }
    return descriptors;
}

/**
 * 解析圆柱等圆曲面的中轴线中点描述符。
 *
 * 设计说明：
 * 1. 圆柱面测量的“中心”语义应落在圆柱轴线上，而不是某一个端部圆边界；
 * 2. 当前拓扑快照尚未持久化解析曲面轴线，因此优先用相邻圆形边界圆心连线确定轴线；
 * 3. 轴向范围取当前面网格点与圆形边界圆心的投影范围，保证返回的是当前选中面的轴线中点。
 *
 * @param input 圆曲面轴线参考解析输入。
 * @returns 圆曲面轴线中点描述符；无法可靠解析时返回 null。
 */
function resolveCircularSurfaceAxisMidpointDescriptor(
    input: CircularSurfaceAxisReferenceInput,
): MeasureCircleDescriptor | null {
    const { context, resultMesh, boundaryDescriptors } = input;
    const { faceSnapshot } = context;
    if (faceSnapshot?.surfaceKind !== 'cylinder') {
        return null;
    }

    const axisDirection = resolveCircularSurfaceAxisDirection(boundaryDescriptors);
    const axisOrigin = boundaryDescriptors[0]?.center;
    if (!axisDirection || !axisOrigin) {
        return null;
    }

    const meshPoints = collectFaceMeshWorldPoints(resultMesh, faceSnapshot.triangleRange);
    const projectedPoints = [
        ...meshPoints,
        ...boundaryDescriptors.map((descriptor) => descriptor.center),
    ];
    if (projectedPoints.length === 0) {
        return null;
    }

    let minProjection = Number.POSITIVE_INFINITY;
    let maxProjection = Number.NEGATIVE_INFINITY;
    projectedPoints.forEach((point) => {
        const projection = point.clone().sub(axisOrigin).dot(axisDirection);
        minProjection = Math.min(minProjection, projection);
        maxProjection = Math.max(maxProjection, projection);
    });

    if (!Number.isFinite(minProjection) || !Number.isFinite(maxProjection)) {
        return null;
    }

    const axisMidpoint = axisOrigin.clone().addScaledVector(
        axisDirection,
        (minProjection + maxProjection) * 0.5,
    );
    return {
        center: axisMidpoint,
        radius: resolveAverageCircularBoundaryRadius(boundaryDescriptors),
        normal: axisDirection,
    };
}

/**
 * 从圆形边界解析圆曲面轴线方向。
 *
 * @param boundaryDescriptors 相邻圆形边界描述符。
 * @returns 轴线单位方向；无法解析时返回 null。
 */
function resolveCircularSurfaceAxisDirection(
    boundaryDescriptors: readonly MeasureCircleDescriptor[],
): THREE.Vector3 | null {
    const centerDirection = resolveAxisDirectionFromBoundaryCenters(boundaryDescriptors);
    if (centerDirection) {
        return centerDirection;
    }

    const boundaryNormal = boundaryDescriptors[0]?.normal;
    if (!boundaryNormal || boundaryNormal.lengthSq() <= CIRCULAR_SURFACE_AXIS_EPSILON) {
        return null;
    }
    return boundaryNormal.clone().normalize();
}

/**
 * 通过边界圆心连线解析轴线方向。
 *
 * @param boundaryDescriptors 相邻圆形边界描述符。
 * @returns 轴线单位方向；圆心不足或重合时返回 null。
 */
function resolveAxisDirectionFromBoundaryCenters(
    boundaryDescriptors: readonly MeasureCircleDescriptor[],
): THREE.Vector3 | null {
    let bestDirection: THREE.Vector3 | null = null;
    let bestDistanceSq = CIRCULAR_SURFACE_AXIS_EPSILON;
    for (let leftIndex = 0; leftIndex < boundaryDescriptors.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < boundaryDescriptors.length; rightIndex += 1) {
            const direction = boundaryDescriptors[rightIndex]!.center
                .clone()
                .sub(boundaryDescriptors[leftIndex]!.center);
            const distanceSq = direction.lengthSq();
            if (distanceSq > bestDistanceSq) {
                bestDistanceSq = distanceSq;
                bestDirection = direction.normalize();
            }
        }
    }
    return bestDirection;
}

/**
 * 计算相邻圆形边界的平均半径。
 *
 * @param boundaryDescriptors 相邻圆形边界描述符。
 * @returns 平均半径。
 */
function resolveAverageCircularBoundaryRadius(
    boundaryDescriptors: readonly MeasureCircleDescriptor[],
): number {
    if (boundaryDescriptors.length === 0) {
        return 0;
    }
    return averageMeterScalars(boundaryDescriptors.map((descriptor) => descriptor.radius));
}

/**
 * 基于参考点解析圆柱曲面“同截面圆”描述符。
 *
 * 设计说明：
 * 1. 混合几何策略下，最大距离/圆心到圆心必须与最小距离圆侧参考点落在同一截面圆；
 * 2. 圆心通过“参考点投影到圆柱轴线”得到，避免回落到端部边界圆心导致偏差；
 * 3. 半径优先使用参考点到轴线的径向距离，退化时回退边界平均半径。
 *
 * @param context 度量上下文。
 * @param referencePoint 最小距离在圆侧的参考点 A。
 * @param boundaryDescriptors 相邻圆形边界描述符。
 * @returns 同截面圆描述符；无法可靠解析时返回 null。
 */
function resolveCylinderSectionDescriptorAtReferencePoint(
    context: MeasureMetricQueryContext,
    referencePoint: THREE.Vector3,
    boundaryDescriptors: readonly MeasureCircleDescriptor[],
): MeasureCircleDescriptor | null {
    const { faceSnapshot } = context;
    if (faceSnapshot?.surfaceKind !== 'cylinder') {
        return null;
    }

    const axisDirection = resolveCircularSurfaceAxisDirection(boundaryDescriptors);
    const axisOrigin = boundaryDescriptors[0]?.center;
    if (!axisDirection || !axisOrigin) {
        return null;
    }

    const signedProjection = referencePoint.clone().sub(axisOrigin).dot(axisDirection);
    const sectionCenter = axisOrigin.clone().addScaledVector(axisDirection, signedProjection);
    const radialDistance = referencePoint.distanceTo(sectionCenter);
    const fallbackRadius = resolveAverageCircularBoundaryRadius(boundaryDescriptors);
    const radius = radialDistance > CIRCULAR_SURFACE_AXIS_EPSILON ? radialDistance : fallbackRadius;
    if (!Number.isFinite(radius) || radius <= CIRCULAR_SURFACE_AXIS_EPSILON) {
        return null;
    }

    return {
        center: sectionCenter,
        radius,
        normal: axisDirection.clone(),
    };
}

/**
 * 基于最小距离圆侧参考点，解析其所在圆弧/圆的拟合描述符。
 *
 * 设计说明：
 * 1. 圆弧/整圆边：对当前选中边采样拟合圆，与对象存储 center 属性无关；
 * 2. 圆曲面：圆柱优先按参考点所在同截面圆解析；失败时回退边界圆近邻匹配。
 *
 * @param context 圆相关几何体的度量上下文。
 * @param referencePoint 最小距离在圆侧的参考点 A。
 * @param geometryKind 圆相关几何类别。
 * @returns 参考点 A 所在圆弧/圆的拟合描述符；无法解析时返回 null。
 */
export function resolveArcCircleDescriptorAtReferencePoint(
    context: MeasureMetricQueryContext,
    referencePoint: THREE.Vector3,
    geometryKind: MeasureGeometryKind,
): MeasureCircleDescriptor | null {
    switch (geometryKind) {
        case MeasureGeometryKind.ArcEdge:
        case MeasureGeometryKind.FullCircleEdge:
            return buildMeasureCircleDescriptorFromEdgeContext(context);
        case MeasureGeometryKind.CircularSurfaceFace: {
            const resultMesh = resolveOwningFeatureResultMesh(context.sceneObject);
            if (!resultMesh) {
                return resolveCircularFaceCircleDescriptorAtReferencePoint(context, referencePoint);
            }
            const boundaryDescriptors = resolveCircularBoundaryDescriptors(context, resultMesh);
            const cylinderSectionDescriptor = resolveCylinderSectionDescriptorAtReferencePoint(
                context,
                referencePoint,
                boundaryDescriptors,
            );
            if (cylinderSectionDescriptor) {
                return cylinderSectionDescriptor;
            }
            return resolveCircularFaceCircleDescriptorAtReferencePoint(context, referencePoint);
        }
        default:
            return null;
    }
}

/**
 * 基于最小距离圆侧参考点，解析其所在圆弧/圆的圆心。
 *
 * @param context 圆相关几何体的度量上下文。
 * @param referencePoint 最小距离在圆侧的参考点 A。
 * @param geometryKind 圆相关几何类别。
 * @returns 参考点 A 所在圆弧/圆的圆心；无法解析时返回 null。
 */
export function resolveArcCircleCenterAtReferencePoint(
    context: MeasureMetricQueryContext,
    referencePoint: THREE.Vector3,
    geometryKind: MeasureGeometryKind,
): THREE.Vector3 | null {
    const descriptor = resolveArcCircleDescriptorAtReferencePoint(
        context,
        referencePoint,
        geometryKind,
    );
    return descriptor?.center.clone() ?? null;
}

/**
 * 由边采样上下文构建拟合圆描述符。
 *
 * @param context 度量上下文。
 * @returns 拟合圆描述符；无法构建时返回 null。
 */
function buildMeasureCircleDescriptorFromEdgeContext(
    context: MeasureMetricQueryContext,
): MeasureCircleDescriptor | null {
    const edgePoints = resolveEdgeSampledWorldPoints(context);
    const edgeCircle = resolveEdgeCircleFit(context);
    if (!edgeCircle) {
        return null;
    }

    const basis = estimatePlaneBasisFromWorldPoints(edgePoints);
    if (!basis) {
        return null;
    }

    const normal = new THREE.Vector3().crossVectors(basis.axisU, basis.axisV).normalize();
    return {
        center: edgeCircle.center.clone(),
        radius: edgeCircle.radius,
        normal,
    };
}

/**
 * 解析圆曲面上与参考点 A 对应的圆形边界边的拟合圆描述符。
 *
 * @param context 度量上下文。
 * @param referencePoint 最小距离在圆侧的参考点 A。
 * @returns 对应圆弧/圆的拟合描述符；无法解析时返回 null。
 */
function resolveCircularFaceCircleDescriptorAtReferencePoint(
    context: MeasureMetricQueryContext,
    referencePoint: THREE.Vector3,
): MeasureCircleDescriptor | null {
    const { sceneObject, faceSnapshot } = context;
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    if (!resultMesh || !faceSnapshot) {
        return null;
    }

    let bestDescriptor: MeasureCircleDescriptor | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    for (const edgeIndex of faceSnapshot.adjacentEdgeIndices) {
        const edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(edgeIndex);
        if (!edgeSnapshot || edgeSnapshot.curveKind !== 'circle') {
            continue;
        }

        const edgeContext: MeasureMetricQueryContext = {
            sceneObject,
            topologyPick: null,
            brepDescriptor: null,
            edgeSnapshot,
            faceSnapshot: null,
        };
        const edgePoints = resolveEdgeSampledWorldPoints(edgeContext);
        const closestOnEdge = closestPointOnPolylineForCircleCenter(referencePoint, edgePoints);
        if (!closestOnEdge) {
            continue;
        }

        const descriptor = buildMeasureCircleDescriptorFromEdgeContext(edgeContext);
        if (!descriptor) {
            continue;
        }

        const distanceSq = referencePoint.distanceToSquared(closestOnEdge);
        if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestDescriptor = descriptor;
        }
    }

    return bestDescriptor;
}

/**
 * 求点到折线的最近点（供圆心解析内部使用）。
 *
 * @param point 查询点。
 * @param points 折线点列。
 * @returns 折线上最近点；无效时返回 null。
 */
function closestPointOnPolylineForCircleCenter(
    point: THREE.Vector3,
    points: readonly THREE.Vector3[],
): THREE.Vector3 | null {
    if (points.length < 2) {
        return null;
    }

    let bestPoint: THREE.Vector3 | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;
    for (let index = 1; index < points.length; index += 1) {
        const candidate = closestPointOnSegmentForCircleCenter(point, points[index - 1]!, points[index]!);
        const distanceSq = candidate.distanceToSquared(point);
        if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestPoint = candidate;
        }
    }

    return bestPoint;
}

/**
 * 求点到线段的最近点（供圆心解析内部使用）。
 *
 * @param point 查询点。
 * @param segmentStart 线段起点。
 * @param segmentEnd 线段终点。
 * @returns 线段上最近点。
 */
function closestPointOnSegmentForCircleCenter(
    point: THREE.Vector3,
    segmentStart: THREE.Vector3,
    segmentEnd: THREE.Vector3,
): THREE.Vector3 {
    const direction = segmentEnd.clone().sub(segmentStart);
    const lengthSq = direction.lengthSq();
    if (lengthSq <= Number.EPSILON) {
        return segmentStart.clone();
    }

    const parameter = THREE.MathUtils.clamp(
        point.clone().sub(segmentStart).dot(direction) / lengthSq,
        0,
        1,
    );
    return segmentStart.clone().add(direction.multiplyScalar(parameter));
}

